import { Link, useParams } from 'react-router-dom'
import { UserButton, useAuth } from '@clerk/clerk-react'
import { useEffect, useMemo, useState } from 'react'
import { trackEvent } from '../analytics'

type Statement = {
  id: string
  filename: string
  status: 'uploaded' | 'processing' | 'completed' | 'failed'
  uploaded_at: number
  processed_at?: number | null
}

type Transaction = {
  id: string
  transaction_date: string | null
  description: string | null
  debit: number | null
  credit: number | null
  balance: number | null
}

export default function StatementDetailsPage() {
  const { id } = useParams()
  const { getToken } = useAuth()
  const apiBase = useMemo(() => import.meta.env.VITE_API_URL || '/api/v1', [])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statement, setStatement] = useState<Statement | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [sortKey, setSortKey] = useState<keyof Transaction>('transaction_date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | 'json' | null>(null)
  const [processing, setProcessing] = useState(false)
  const [progressStart, setProgressStart] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState<number>(0)

  const formatDate = (ts?: number | null) => {
    if (!ts) return '-'
    const d = new Date((ts as number) * 1000)
    return d.toISOString().slice(0, 10)
  }

  const load = async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const [sRes, tRes] = await Promise.all([
        fetch(`${apiBase}/statements/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/statements/${id}/transactions`, { headers: { Authorization: `Bearer ${token}` } })
      ])
      if (!sRes.ok) throw new Error(await sRes.text())
      if (!tRes.ok) throw new Error(await tRes.text())
      const s = await sRes.json()
      const t = await tRes.json()
      setStatement(s.statement)
      setTransactions(t.transactions || [])
      setPage(1)
    } catch (e: any) {
      setError(e.message || 'Failed to load statement')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Poll if processing
  useEffect(() => {
    if (!statement) return
    if (statement.status !== 'processing') return
    if (progressStart === null) setProgressStart(Date.now())
    const timer = setInterval(() => load(), 5000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statement?.status])

  // Elapsed timer for progress UI
  useEffect(() => {
    if (progressStart === null) return
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - progressStart) / 1000)), 1000)
    return () => clearInterval(t)
  }, [progressStart])

  const triggerProcess = async () => {
    if (!id) return
    try {
      setProcessing(true)
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch(`${apiBase}/statements/${id}/process`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(await res.text())
      setProgressStart(Date.now())
      trackEvent('extract_started', { statement_id: id, source: 'details' })
      await load()
    } catch (e: any) {
      alert(`Process failed: ${e.message}`)
    } finally {
      setProcessing(false)
    }
  }

  const triggerExport = async (format: 'csv' | 'xlsx' | 'json') => {
    if (!id) return
    try {
      setExporting(format)
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch(`${apiBase}/statements/${id}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ format }),
      })
      if (!res.ok) throw new Error(await res.text())
      const dl = await fetch(`${apiBase}/statements/${id}/export/${format}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!dl.ok) throw new Error(await dl.text())
      const blob = await dl.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `statement-${id}.${format}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      trackEvent('export', { format, statement_id: id })
    } catch (e: any) {
      trackEvent('export_error', {
        format,
        message: String(e?.message || e).slice(0, 120),
      })
      alert(`Export failed: ${e.message}`)
    } finally {
      setExporting(null)
    }
  }

  const sorted = useMemo(() => {
    const arr = [...transactions]
    arr.sort((a, b) => {
      const valA = a[sortKey]
      const valB = b[sortKey]
      let cmp = 0
      if (sortKey === 'debit' || sortKey === 'credit' || sortKey === 'balance') {
        const na = typeof valA === 'number' ? (valA as number) : Number.NEGATIVE_INFINITY
        const nb = typeof valB === 'number' ? (valB as number) : Number.NEGATIVE_INFINITY
        cmp = na === nb ? 0 : na < nb ? -1 : 1
      } else if (sortKey === 'transaction_date') {
        const sa = (valA || '') as string
        const sb = (valB || '') as string
        cmp = sa.localeCompare(sb)
      } else {
        const sa = (valA || '') as string
        const sb = (valB || '') as string
        cmp = sa.localeCompare(sb)
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [transactions, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize
    return sorted.slice(start, start + pageSize)
  }, [sorted, page, pageSize])

  const setSort = (key: keyof Transaction) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-indigo-600">
              BankStatementReader
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="px-4 py-2 text-gray-700">
                Dashboard
              </Link>
              <UserButton />
            </div>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Statement Details</h1>
            <p className="text-gray-600">ID: {id}</p>
          </div>
          <div>
            <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-700">← Back to Dashboard</Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded">{error}</div>
        )}

        {loading || !statement ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Filename</div>
                  <div className="font-medium">{statement.filename}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      statement.status === 'completed' ? 'bg-green-100 text-green-700' :
                      statement.status === 'failed' ? 'bg-red-100 text-red-700' :
                      statement.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {statement.status}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Uploaded</div>
                  <div className="font-medium">{formatDate(statement.uploaded_at)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Processed</div>
                  <div className="font-medium">{formatDate(statement.processed_at)}</div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 items-center">
                {(statement.status === 'uploaded' || statement.status === 'failed') && (
                  <button
                    onClick={triggerProcess}
                    disabled={processing}
                    className={`px-4 py-2 rounded text-white ${processing ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                  >
                    {processing ? 'Processing…' : (statement.status === 'failed' ? 'Re-process' : 'Process Now')}
                  </button>
                )}

                {statement.status === 'processing' && (
                  <div className="flex items-center gap-3 text-yellow-700">
                    <div className="h-4 w-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                    <div className="w-56 h-2 bg-yellow-100 rounded overflow-hidden">
                      <div className="h-2 bg-yellow-400 animate-pulse w-full" />
                    </div>
                    <div className="text-sm">{elapsed}s elapsed</div>
                  </div>
                )}

                <button
                  onClick={() => triggerExport('csv')}
                  disabled={statement.status !== 'completed' || exporting !== null}
                  className={`px-4 py-2 rounded ${statement.status !== 'completed' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                >CSV</button>
                <button
                  onClick={() => triggerExport('xlsx')}
                  disabled={statement.status !== 'completed' || exporting !== null}
                  className={`px-4 py-2 rounded ${statement.status !== 'completed' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                >XLSX</button>
                <button
                  onClick={() => triggerExport('json')}
                  disabled={statement.status !== 'completed' || exporting !== null}
                  className={`px-4 py-2 rounded ${statement.status !== 'completed' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                >JSON</button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Transactions ({transactions.length})</h2>
                <button onClick={load} className="text-sm text-indigo-600 hover:text-indigo-700">Refresh</button>
              </div>
              {statement.status !== 'completed' && (
                <div className="mb-4 text-yellow-800 bg-yellow-50 border border-yellow-200 p-3 rounded">
                  {statement.status === 'processing' ? 'OCR is processing. This page will refresh periodically…' : 'Transactions will appear after processing.'}
                </div>
              )}
              {transactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No transactions found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead>
                      <tr className="text-gray-600">
                        <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('transaction_date')}>Date {sortKey==='transaction_date' ? (sortDir==='asc' ? '▲' : '▼') : ''}</th>
                        <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('description')}>Description {sortKey==='description' ? (sortDir==='asc' ? '▲' : '▼') : ''}</th>
                        <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('debit')}>Debit {sortKey==='debit' ? (sortDir==='asc' ? '▲' : '▼') : ''}</th>
                        <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('credit')}>Credit {sortKey==='credit' ? (sortDir==='asc' ? '▲' : '▼') : ''}</th>
                        <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('balance')}>Balance {sortKey==='balance' ? (sortDir==='asc' ? '▲' : '▼') : ''}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map((t) => (
                        <tr key={t.id} className="border-t">
                          <td className="py-2 px-3 whitespace-nowrap">{t.transaction_date || '-'}</td>
                          <td className="py-2 px-3">{t.description || '-'}</td>
                          <td className="py-2 px-3">{t.debit ?? '-'}</td>
                          <td className="py-2 px-3">{t.credit ?? '-'}</td>
                          <td className="py-2 px-3">{t.balance ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-600">
                      Page {page} of {totalPages} · Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, sorted.length)} of {sorted.length}
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-600">Rows:</label>
                      <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1) }} className="border rounded px-2 py-1 text-sm">
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <button disabled={page<=1} onClick={() => setPage(p => Math.max(1, p-1))} className={`px-3 py-1 rounded ${page<=1 ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}>Prev</button>
                      <button disabled={page>=totalPages} onClick={() => setPage(p => Math.min(totalPages, p+1))} className={`px-3 py-1 rounded ${page>=totalPages ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}>Next</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
